import React from 'react';
import TableRowActionMenu from '../../../BaseComponents/TableRowActionMenu';
import { formatInTimeZone } from 'date-fns-tz';
import RangeChart from './RangeChart';
import RangeSummary from './RangeSummary';
import {
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO,
  GenderRangeData,
  UserGenderEnum
} from '../../../../types/apiGeneratedTypes';
import { RootState } from '../../../../store/store';
import { useSelector, useDispatch } from 'react-redux';

import {
  setAppointmentIdToEditForTestResults,
  setSelectedBiomarkerResult,
  setShowBiomarkerTestResultModalVisibility,
  setTestResultsModalMode,
  changeTestResultsModalVisibility
} from '../../../../store/memberSlice';

interface TestResultsTableRowProps {
  resultData: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO;
}

const TestResultsTableRow: React.FC<TestResultsTableRowProps> = ({
  resultData
}) => {
  const dispatch = useDispatch();
  const onOptionClick = async (id: number, option: any) => {
    if (option === 'edit') {
      dispatch(setSelectedBiomarkerResult(resultData));
      dispatch(setTestResultsModalMode('update'));
      dispatch(changeTestResultsModalVisibility(true));
    }
  };

  const { memberData } = useSelector((state: RootState) => state.member);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false"); 
  const {admin: { role }} = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));   
  const getRangeData = () => {
    const male = resultData.biomarker.male_range_data;
    const female = resultData.biomarker.female_range_data;

    const isValid = (data: any): data is GenderRangeData =>
      data && !Array.isArray(data) && Object.keys(data).length > 0;

    if (memberData?.gender === UserGenderEnum.Male) {
      if (isValid(male)) return male;
    }

    if (memberData?.gender === UserGenderEnum.Female) {
      if (isValid(female)) return female;
    }

    if (isValid(male)) return male;
    if (isValid(female)) return female;

    return null;
  };

  const rangeData = getRangeData();

  const getBiomarkerDisplayValue = (biomarker: any, result: any) => {
    if (biomarker.biomarker_type === 'BINARY') {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }
    
    if (biomarker.name.toLowerCase().includes('anti-nuclear')) {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }
    
    return `${result.value} ${biomarker.measurement_unit}`;
  };

  const onRowClick = () => {
    dispatch(setSelectedBiomarkerResult(resultData));
    dispatch(setShowBiomarkerTestResultModalVisibility(true));
    dispatch(
      setAppointmentIdToEditForTestResults(
        resultData.result.appointment_unique_id
      )
    );
  };

  return (
    <div
      className='table-row published-test-results-table-row'
      onClick={onRowClick}
    >
      <div className='date'>
        {formatInTimeZone(
          new Date(resultData.result.appointment_date),
          'UTC',
          'dd/MM/yyyy'
        )}
      </div>
     
      <div className='biomarker-name'>
        <span className='biomarker-name__title'>{resultData.biomarker.name}</span>
        {/* {resultData.biomarker.description && (
          <span
            className='biomarker-name__description'
            title={resultData.biomarker.description}
          >
            {resultData.biomarker.description}
          </span>
        )} */}
      </div>
      <div className='type'>
        {resultData.biomarker.is_derived ? (
          <div className='derived-tag'>Derived</div>
        ) : (
          <div className='primary-tag'>Primary</div>
        )}
      </div>
      <div className='body-system'>
        {resultData.biomarker.body_systems?.map((system, index) => (
          <span key={system.id}>
            {system.title}
            {index < resultData.biomarker.body_systems.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
      <div className='biomarker-value' title={getBiomarkerDisplayValue(resultData.biomarker, resultData.result)}>
        {getBiomarkerDisplayValue(resultData.biomarker, resultData.result)}
      </div>
      <div className='chart'>
        <div className='range-wrapper'>
          <RangeChart
            biomarker={resultData.biomarker}
            value={resultData.result.value}
            scale={rangeData}
          />
        </div>
      </div>
      <div className='summary'>
        <RangeSummary
          biomarker={resultData.biomarker}
          value={resultData.result.value}
          scale={rangeData}
        />
      </div>
      <div
        className='biomarker-description'
        title={resultData.biomarker.description || undefined}
      >
        {resultData.biomarker.description}
      </div>
      <div className='actions'>
        {visible && <TableRowActionMenu
          options={[
            { key: 'edit', value: 'Edit' }
          ]}
          id={resultData.result.id}
          onActionMenuClick={onOptionClick}
        />}
      </div>
    </div>
  );
};

export default TestResultsTableRow;
